import { useEffect, useRef } from 'react';
import type { ReactionDuelView } from '@minigame-race/contracts';
import { color } from '../theme.js';

/**
 * Reaction Duel — tap as fast as you can once the panel turns GREEN.
 *
 * Ping-fair: we measure the reaction LOCALLY — from the frame we actually render
 * green (`greenShownAt`, a requestAnimationFrame-confirmed timestamp) to the tap
 * — and send that delta. Since it's a local difference, network latency cancels
 * out entirely. Tapping before green is a false start (sends `reactionMs: null`).
 */
export function ReactionDuelBoard({
  view,
  onMove,
}: {
  view: ReactionDuelView;
  onMove: (move: unknown) => void;
}) {
  const { phase, round, totalRounds, wins, yourSeat, youTapped, lastRound } = view;
  const youWins = wins[yourSeat];
  const rivalWins = wins[yourSeat === 0 ? 1 : 0];

  // The instant THIS client painted green. Set via rAF so it reflects the real
  // rendered frame, not the state update. Reset whenever we (re)enter live.
  const greenAt = useRef<number | null>(null);
  useEffect(() => {
    if (phase === 'live') {
      greenAt.current = null;
      const id = requestAnimationFrame(() => { greenAt.current = performance.now(); });
      return () => cancelAnimationFrame(id);
    }
    greenAt.current = null;
    return undefined;
  }, [phase, round]);

  const tap = (): void => {
    if (youTapped) return;
    if (phase === 'waiting') {
      onMove({ type: 'tap', reactionMs: null }); // false start
    } else if (phase === 'live') {
      const start = greenAt.current;
      const ms = start === null ? 0 : Math.max(0, Math.round(performance.now() - start));
      onMove({ type: 'tap', reactionMs: ms });
    }
  };

  const panel = panelStyle(phase, youTapped);

  return (
    <div style={{ width: 'min(92cqw, 78cqh, 460px)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'min(2cqh,16px)' }}>
      {/* scoreboard */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Score label="YOU" value={youWins} tint={color.lime} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, letterSpacing: '.24em', color: 'rgba(234,255,234,.4)' }}>ROUND</div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 16, fontWeight: 800 }}>{Math.min(round + 1, totalRounds)}/{totalRounds}</div>
        </div>
        <Score label="RIVAL" value={rivalWins} tint={color.cyan} align="right" />
      </div>

      {/* the tap panel */}
      <button
        type="button"
        onClick={tap}
        disabled={youTapped && phase !== 'live'}
        aria-label={phase === 'live' ? 'Tap now' : 'Reaction panel'}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '3 / 2',
          borderRadius: 20,
          border: `2px solid ${panel.border}`,
          background: panel.bg,
          color: panel.fg,
          cursor: phase === 'over' ? 'default' : 'pointer',
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
          transition: 'background .08s ease, border-color .08s ease',
          touchAction: 'manipulation',
          animation: phase === 'live' && !youTapped ? 'rdGoPulse .5s ease-out' : undefined,
        }}
      >
        <PanelContent view={view} youTapped={youTapped} youSeat={yourSeat} />
      </button>

      <div style={{ textAlign: 'center', fontSize: 12, color: 'rgba(234,255,234,.45)', minHeight: 16 }}>
        {phase === 'waiting' && (youTapped ? 'Too early! Wait for green next round.' : 'Wait for GREEN, then tap!')}
        {phase === 'live' && (youTapped ? 'Tapped — waiting for your rival…' : 'TAP NOW!')}
        {phase === 'reveal' && lastRound && revealCaption(lastRound, yourSeat)}
        {phase === 'over' && 'Duel complete.'}
      </div>
    </div>
  );
}

function PanelContent({ view, youTapped, youSeat }: { view: ReactionDuelView; youTapped: boolean; youSeat: 0 | 1 }) {
  const { phase, lastRound } = view;

  if (phase === 'waiting') {
    return <Big text={youTapped ? '✕' : 'WAIT'} sub={youTapped ? 'FALSE START' : 'get ready…'} />;
  }
  if (phase === 'live') {
    return <Big text={youTapped ? '…' : 'TAP!'} sub={youTapped ? 'waiting for rival' : ''} big />;
  }
  if (phase === 'reveal' && lastRound) {
    const yours = lastRound.yourMs;
    const theirs = lastRound.opponentMs;
    const wonRound = lastRound.winnerSeat === youSeat;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 'min(6cqw,22px)', fontWeight: 800, letterSpacing: '.1em', color: wonRound ? color.lime : lastRound.winnerSeat === null ? color.amberBright : color.redBright }}>
          {lastRound.winnerSeat === null ? 'TIE' : wonRound ? 'YOU WIN' : 'RIVAL WINS'}
        </span>
        <div style={{ display: 'flex', gap: 'min(6cqw,28px)', fontFamily: "'JetBrains Mono',monospace" }}>
          <TimeChip label="YOU" ms={yours} tint={color.lime} highlight={wonRound} />
          <TimeChip label="RIVAL" ms={theirs} tint={color.cyan} highlight={lastRound.winnerSeat === (youSeat === 0 ? 1 : 0)} />
        </div>
      </div>
    );
  }
  // over: final tally handled by the outer result screen; show a calm state.
  return <Big text="DONE" sub="" />;
}

function Big({ text, sub, big }: { text: string; sub: string; big?: boolean }) {
  return (
    <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: big ? 'min(16cqw,72px)' : 'min(11cqw,52px)', fontWeight: 900, letterSpacing: '.06em', lineHeight: 1 }}>{text}</span>
      {sub && <span style={{ fontSize: 'min(3.4cqw,14px)', letterSpacing: '.1em', opacity: 0.7 }}>{sub}</span>}
    </div>
  );
}

function TimeChip({ label, ms, tint, highlight }: { label: string; ms: number | null; tint: string; highlight: boolean }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 10, letterSpacing: '.18em', color: 'rgba(255,255,255,.55)' }}>{label}</div>
      <div style={{ fontSize: 'min(6cqw,24px)', fontWeight: 800, color: highlight ? tint : 'rgba(255,255,255,.7)' }}>
        {ms === null ? '—' : `${ms}ms`}
      </div>
    </div>
  );
}

function Score({ label, value, tint, align }: { label: string; value: number; tint: string; align?: 'right' }) {
  return (
    <div style={{ textAlign: align === 'right' ? 'right' : 'left' }}>
      <div style={{ fontSize: 10, letterSpacing: '.2em', color: 'rgba(234,255,234,.4)' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: tint, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function revealCaption(lastRound: NonNullable<ReactionDuelView['lastRound']>, youSeat: 0 | 1): string {
  if (lastRound.falseStartSeat === youSeat) return 'You jumped the gun.';
  if (lastRound.falseStartSeat !== null) return 'Rival false-started!';
  if (lastRound.winnerSeat === null) return 'Dead heat!';
  return lastRound.winnerSeat === youSeat ? 'Faster!' : 'Rival was quicker.';
}

/** Panel colours per phase. Red-ish while waiting, GREEN when live. */
function panelStyle(phase: ReactionDuelView['phase'], youTapped: boolean): { bg: string; border: string; fg: string } {
  if (phase === 'live' && !youTapped) {
    return { bg: 'radial-gradient(120% 120% at 50% 40%, #38ff6a, #12c24a)', border: '#7dffa0', fg: '#04140a' };
  }
  if (phase === 'waiting') {
    return youTapped
      ? { bg: 'rgba(255,46,77,.22)', border: 'rgba(255,46,77,.6)', fg: color.redBright }
      : { bg: 'rgba(120,20,30,.35)', border: 'rgba(255,46,77,.4)', fg: 'rgba(255,180,190,.9)' };
  }
  // reveal / live-after-tap / over
  return { bg: 'rgba(0,0,0,.4)', border: 'rgba(166,255,46,.2)', fg: color.text };
}
